/**
 * Analytics 2.0 — per-post performance dashboard.
 * Attempts real metrics via Meta / YouTube Insights APIs (LinkedIn pending
 * Marketing API app review); falls back to simulated numbers with a clear
 * badge and offers a one-click refresh on the detail view.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Eye, Heart, MessageCircle, Share2, Bookmark, Users, Loader2, Sparkles, RefreshCcw, Radio, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

interface Summary {
  days: number;
  total_posts: number;
  total: { impressions: number; reach: number; likes: number; comments: number; shares: number; saves: number };
  by_platform: any[];
  top_posts: { job_id: string; caption: string; created_at: string; impressions: number; reach: number; likes: number; shares: number; engagement: number; overall_status: string }[];
  any_real?: boolean;
}

const RANGES = [{ label: "7d", d: 7 }, { label: "30d", d: 30 }, { label: "90d", d: 90 }];

const SourceBadge: React.FC<{ real: boolean; testId?: string }> = ({ real, testId }) => (
  <span
    data-testid={testId}
    title={real ? "Real metrics fetched from the platform Insights API (cached up to 1h)" : "Simulated — connect insights scopes on Meta/YouTube to unlock real numbers"}
    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold rounded-full px-2 py-0.5 ${real ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`}
  >
    {real ? <Radio className="h-2.5 w-2.5" /> : <Zap className="h-2.5 w-2.5" />}
    {real ? "Real" : "Simulated"}
  </span>
);

const KpiCard: React.FC<{ label: string; value: number; icon: React.ReactNode; testId?: string }> = ({ label, value, icon, testId }) => (
  <div className="sg-card p-5" data-testid={testId}>
    <div className="flex items-start gap-2">
      <div className="h-8 w-8 rounded-lg bg-[color:var(--sb-saffron)]/10 flex items-center justify-center text-[color:var(--sb-saffron)]">{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">{label}</div>
        <div className="font-display font-bold text-2xl tabular-nums">{value.toLocaleString()}</div>
      </div>
    </div>
  </div>
);

export const Analytics2: React.FC = () => {
  const [days, setDays] = useState(30);
  const { user } = useAuth();
  const qc = useQueryClient();
  const canPreviewDigest = !!(user as any)?.is_super_admin || user?.role === "admin";
  const q = useQuery<Summary>({
    queryKey: ["analytics-summary", days],
    queryFn: async () => (await api.get(`/composer/analytics/summary?days=${days}`)).data,
  });
  const digestPreview = useMutation({
    mutationFn: async () => (await api.post("/composer/admin/digest-test", {})).data,
    onSuccess: (d) => toast.success(`Weekly digest preview sent to ${d.sent_to}`),
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Digest preview failed — check SMTP config"),
  });

  const refreshAll = useMutation({
    mutationFn: async () => {
      // Trigger a live refresh on each of the top posts — we intentionally cap
      // at 8 to stay within Meta rate limits.
      const top = q.data?.top_posts?.slice(0, 8) || [];
      const results = await Promise.allSettled(
        top.map((p) => api.post(`/composer/analytics/post/${p.job_id}/refresh`)),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      return { ok, total: top.length };
    },
    onSuccess: ({ ok, total }) => {
      toast.success(`Refreshed ${ok}/${total} posts from platform Insights APIs`);
      qc.invalidateQueries({ queryKey: ["analytics-summary"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Refresh failed"),
  });

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="analytics-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-[color:var(--sb-saffron)]" /> Analytics
            </h1>
            <p className="text-sm text-[color:var(--sg-muted)] mt-1">Post-level performance across every platform you publish to.</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <SourceBadge real={!!q.data?.any_real} testId="analytics-source-badge" />
            <Button
              variant="outline"
              size="sm"
              data-testid="analytics-refresh-btn"
              onClick={() => refreshAll.mutate()}
              disabled={refreshAll.isPending || !q.data?.top_posts?.length}
              title="Force a live pull from Meta / YouTube Insights (up to 8 recent posts, cached for 1 hour)"
            >
              {refreshAll.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />}
              Refresh real metrics
            </Button>
            {canPreviewDigest && (
              <Button
                variant="outline"
                size="sm"
                data-testid="digest-preview-btn"
                onClick={() => digestPreview.mutate()}
                disabled={digestPreview.isPending}
                title="Emails you an AI-generated preview of the weekly wrap-up right now"
                className="border-[color:var(--sb-saffron)] text-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron)]/10"
              >
                {digestPreview.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                Send me a preview
              </Button>
            )}
            <div className="flex gap-1 items-center rounded-full border border-[color:var(--sg-border)] p-1">
              {RANGES.map((r) => (
                <button key={r.d} onClick={() => setDays(r.d)} data-testid={`analytics-range-${r.label}`}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${days === r.d ? "bg-[color:var(--sg-fg)] text-white" : "text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {q.isLoading ? (
          <div className="sg-card p-12 text-center text-sm text-[color:var(--sg-muted)]"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiCard label="Impressions" value={q.data?.total.impressions || 0} icon={<Eye className="h-4 w-4" />} testId="kpi-impressions" />
              <KpiCard label="Reach" value={q.data?.total.reach || 0} icon={<Users className="h-4 w-4" />} testId="kpi-reach" />
              <KpiCard label="Likes" value={q.data?.total.likes || 0} icon={<Heart className="h-4 w-4" />} testId="kpi-likes" />
              <KpiCard label="Comments" value={q.data?.total.comments || 0} icon={<MessageCircle className="h-4 w-4" />} testId="kpi-comments" />
              <KpiCard label="Shares" value={q.data?.total.shares || 0} icon={<Share2 className="h-4 w-4" />} testId="kpi-shares" />
              <KpiCard label="Saves" value={q.data?.total.saves || 0} icon={<Bookmark className="h-4 w-4" />} testId="kpi-saves" />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="sg-card p-5" data-testid="analytics-by-platform">
                <div className="font-display font-bold mb-4">By platform</div>
                {(!q.data?.by_platform || q.data.by_platform.length === 0) ? (
                  <div className="text-xs text-[color:var(--sg-muted)] py-6 text-center">No posts yet. Compose your first with the wizard.</div>
                ) : (
                  <div className="space-y-3">
                    {q.data.by_platform.map((p: any) => {
                      const maxImpr = Math.max(...(q.data?.by_platform || []).map((x: any) => x.impressions));
                      const pct = maxImpr > 0 ? (p.impressions / maxImpr) * 100 : 0;
                      const realCount = p.real_sources || 0;
                      const platformReal = realCount > 0 && realCount === p.posts;
                      const platformMixed = realCount > 0 && realCount < p.posts;
                      return (
                        <div key={p.platform} className="text-xs" data-testid={`analytics-platform-${p.platform}`}>
                          <div className="flex justify-between mb-0.5 items-center gap-2 flex-wrap">
                            <span className="font-semibold capitalize inline-flex items-center gap-1.5">
                              {p.platform}
                              <span className="text-[color:var(--sg-muted)] font-normal">· {p.posts} posts</span>
                              {platformReal ? <SourceBadge real={true} /> : platformMixed ? (
                                <span className="text-[9px] uppercase tracking-widest font-semibold rounded-full px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200" title="Some posts have real metrics; others fell back to simulated">
                                  {realCount}/{p.posts} real
                                </span>
                              ) : <SourceBadge real={false} />}
                            </span>
                            <span className="text-[color:var(--sg-muted)] tabular-nums">{p.impressions.toLocaleString()} impr · {p.likes.toLocaleString()} likes</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
                            <div className="h-full bg-[color:var(--sb-saffron)]" style={{ width: `${Math.max(4, pct)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="sg-card p-5" data-testid="analytics-top-posts">
                <div className="font-display font-bold mb-4">Top posts by engagement</div>
                {(!q.data?.top_posts || q.data.top_posts.length === 0) ? (
                  <div className="text-xs text-[color:var(--sg-muted)] py-6 text-center">No posts in this window.</div>
                ) : (
                  <div className="divide-y divide-[color:var(--sg-border)]">
                    {q.data.top_posts.slice(0, 8).map((p) => (
                      <Link key={p.job_id} to={`/posts?highlight=${p.job_id}`} data-testid={`top-post-${p.job_id}`} className="py-2 flex items-center gap-3 hover:bg-[color:var(--sg-muted-bg)] -mx-3 px-3 rounded">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold truncate">{p.caption || "(untitled)"}</div>
                          <div className="text-[10px] text-[color:var(--sg-muted)]">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""} · {p.overall_status}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-display font-bold tabular-nums text-sm">{p.engagement.toLocaleString()}</div>
                          <div className="text-[9px] uppercase tracking-wider text-[color:var(--sg-muted)]">eng</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="text-[11px] text-[color:var(--sg-muted)] text-center" data-testid="analytics-note">
              {q.data?.any_real
                ? "Real metrics from Meta Graph & YouTube Data APIs, cached for 1 hour. Click Refresh to force a live pull."
                : "Showing simulated metrics. Connect Instagram/Facebook with the insights scope, or YouTube with youtube.readonly, then click Refresh to unlock real numbers. LinkedIn insights pending Marketing API app review."}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Analytics2;
